import {useCallback, useEffect, useMemo, useState} from 'react';
import {format} from 'date-fns';
import {QueryFunctionContext, useQuery} from 'react-query';

import Typography from 'components/Typography';
import StatusBadge from 'components/StatusBadge';
import ViewDetails from 'components/ViewDetails';

import {
  Address,
  Customer,
  DataItem,
  DataTableSchemaItem,
  ListResponse,
  Product,
  Request,
  SelectOption,
} from 'types';
import useSort from 'hooks/useSort';
import api from 'api';
import {
  mapStatusColor,
  mapStatusText,
  mapMMStatusText,
  mapMMSubStatusColor,
} from 'constants/mappings';
import {get, reject, sortBy as sortLodash} from 'lodash';
import {FilterOption} from 'pages/DeliveryPlanner/components/Filter/Filter';
import {useHistory, useRouteMatch} from 'react-router-dom';
import toast from 'react-hot-toast';
import CheckIcon from 'pages/Settings/Users/CheckIcon';
import classNames from 'classnames';
import styles from './Requests.module.scss';
import useAuth from 'hooks/useAuth';

async function getRequests({queryKey}: QueryFunctionContext<any[]>) {
  const [
    ,
    page,
    order,
    status,
    userId,
    keyWord,
    source,
    date,
    billdate,
    createdDate,
    location,
    preference_time,
    onlyOTC,
    paid,
    statuses,
    type,
    prescription,
    eventType,
    redispatch,
    pageSize,
    deliveredBy,
    isUrgentFilter,
    status_date,
  ] = queryKey;

  const notType = ['True', 'False', 'meta', 'landing_page'];

  const paramsObject = {
    order: order,
    status: status || undefined,

    have_prescription:
      source === 'app' &&
      (type?.value === 'landing_page' || type?.value === 'meta_ads')
        ? prescription?.value.toLowerCase() || undefined
        : undefined,
    request_origin:
      source === 'app' &&
      type?.value !== 'True' &&
      type?.value !== 'False' &&
      type?.value
        ? type?.value
        : undefined,
    request_type: (!notType.includes(type?.value) && type?.value) || undefined,
    meta_event_type:
      (type?.value === 'meta_ads' ? eventType : undefined) || undefined,
    user_id: userId || undefined,
    offset: +page * pageSize,
    search: keyWord || undefined,
    limit: pageSize,
    source: source,
    preference_date:
      source !== 'app' && date ? format(date, 'yyyy-MM-dd') : undefined,
    bill_time_after:
      source !== 'app' && billdate.start
        ? format(billdate.start, 'yyyy-MM-dd')
        : undefined,
    bill_time_before:
      source !== 'app' && billdate.end
        ? format(billdate.end, 'yyyy-MM-dd')
        : undefined,
    start_date: createdDate.start
      ? format(createdDate.start, 'yyyy-MM-dd')
      : undefined,
    end_date: createdDate.end
      ? format(createdDate.end, 'yyyy-MM-dd')
      : undefined,
    zip_code:
      location.length > 0
        ? location.reduce((pr: [], curr: any) => [...pr, curr.label], [])
        : undefined,
    closed_statuses:
      statuses.length > 0 && status === 'completed'
        ? statuses.reduce((pr: [], curr: any) => [...pr, curr.value], [])
        : undefined,
    preference_time:
      source !== 'app' && preference_time ? preference_time.value : undefined,
    attempt_start: redispatch
      ? redispatch.value === '5'
        ? '5'
        : redispatch.value
      : undefined,
    attempt_end: redispatch
      ? redispatch.value === '5'
        ? ''
        : redispatch.value
      : undefined,
    only_otc:
      source !== 'app' && onlyOTC ? onlyOTC.value === 'true' : undefined,
    is_paid: source !== 'app' && paid ? paid.value === 'true' : undefined,
    delivered_by: deliveredBy ? deliveredBy.value : undefined,
    is_urgent_delivery: isUrgentFilter
      ? isUrgentFilter.value === 'true'
      : undefined,
    status_date: status_date ? format(status_date, 'yyyy-MM-dd') : undefined,
  };

  const normalizedParams = Object.fromEntries(
    Object.entries(paramsObject).filter(
      ([_, value]) => value !== undefined && value !== ''
    )
  );
  const {data} = await api.get<ListResponse<Request>>('/requests', {
    params: normalizedParams,
  });
  return data as ListResponse<Request>;
}

const SCHEMA: DataTableSchemaItem[] = [
  {dataKey: 'number', header: 'Request #', sortable: false},
  // {dataKey: 'rx_no', header: 'Rx #', sortable: false},
  {dataKey: 'sync_time', header: 'Sync time', sortable: true},
  {dataKey: 'customer', header: 'Patient', sortable: true},
  {dataKey: 'status', header: 'Status', sortable: true},
  {
    dataKey: 'drug_name',
    header: 'Drug name',
    sortable: false,
    colWidth: '270px',
  },
  {
    dataKey: 'notes',
    header: 'Notes',
    sortable: false,
  },
];
const MM_SCHEMA: DataTableSchemaItem[] = [
  {
    dataKey: 'checkbox',
    header: <div style={{height: 40}}></div>,
    sortable: false,
    colWidth: '80px',
    index: -1,
  },
  {dataKey: 'rx_no', header: 'Rx #', sortable: true, index: 0},
  {dataKey: 'customer', header: 'Patient', sortable: true, index: 1},
  {
    dataKey: 'status',
    header: 'Status',
    sortable: true,
    index: 2,
    align: 'center',
  },
  {
    dataKey: 'drug_name',
    header: 'Drug name',
    sortable: false,
    index: 3,
    colWidth: '220px',
  },
  {
    dataKey: 'amount_due',
    header: 'Amount',
    sortable: false,
    index: 4,
    align: 'center',
  },
  {
    dataKey: 'number',
    header: 'Order #',
    sortable: true,
    align: 'center',
    index: 5,
  },
  {
    dataKey: 'sync_time',
    header: 'Sync Time',
    sortable: true,
    align: 'center',
    index: 6,
  },
  {
    dataKey: 'bill_time',
    header: 'Bill Time',
    sortable: false,
    colWidth: '150px',
    index: 7,
  },
  {
    dataKey: 'preference_date',
    header: 'Preference date',
    sortable: false,
    index: 8,
  },
  {
    dataKey: 'attempt',
    header: 'Attempt',
    sortable: true,
    align: 'center',
    index: 9,
  },
  {
    dataKey: 'notes',
    header: 'Notes',
    sortable: false,
    index: 10,
    colWidth: '200px',
  },
];

const MMtabs = [
  {title: 'All', value: ''},
  {title: 'Unbilled', value: 'completed'},
  {title: 'Billed', value: 'billed'},
  {title: 'Filled', value: 'filled'},
  {title: 'OTC CheckOut', value: 'otc_status'},
  {title: 'Waiting for Pickup', value: 'waiting_for_pickup'},
  {title: 'Ready for Delivery', value: 'ready_for_delivery'},
  {title: 'Delivery Partner', value: 'delivery_partner'},
  {title: 'Picked-Up', value: 'picked_up'},
  {title: 'Delivered', value: 'delivered'},
  {title: 'Skipped', value: 'cancel_script'},
  {title: 'Archived', value: 'archive'},
];

export const disabledStatus = ['delivered', 'picked_up'];

const tabs = [
  {title: 'All', value: ''},
  {title: 'New', value: 'new'},
  {title: 'In-Progress', value: 'in_progress'},
  {title: 'Closed', value: 'completed'},
  {title: 'Incomplete', value: 'incomplete'},
];

type RequestFilter = {
  userId?: string;
  source?: string;
  page?: number;
};

function useRequests(filterData: RequestFilter) {
  const history = useHistory();
  const {rolePermission} = useAuth();
  const {params} = useRouteMatch<{page: string | undefined}>();
  const [currentPage, setCurrentPage] = useState(parseInt(params.page || '1'));
  const {sortBy, sortOrder, order, onSort} = useSort('id', 'desc');
  const [source] = useState(filterData.source || '');
  const [pageSize, setPageSize] = useState(40);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState(
    sessionStorage.getItem('state') === 'all'
      ? ''
      : sessionStorage.getItem(
          source === 'app' ? 'requests_state' : 'orders_state'
        ) || ''
  );
  const [key, setKeyWord] = useState(
    sessionStorage.getItem(`searchKey_${source}`) || ''
  );
  const [keyWord, setApiKeyWord] = useState(
    sessionStorage.getItem(`searchKey_${source}`) || ''
  );
  const initialLocation = sessionStorage.getItem(`location_${source}`)
    ? JSON.parse(sessionStorage.getItem(`location_${source}`) as any)
    : [];
  const [location, setLocation] = useState<FilterOption[]>(initialLocation);

  const initialStatuses = sessionStorage.getItem(`request_statuses`)
    ? JSON.parse(sessionStorage.getItem(`request_statuses`) as any)
    : [];
  const [statuses, setStatuses] = useState<FilterOption[]>(initialStatuses);
  const initialTypes = sessionStorage.getItem(`request_types_${source}`)
    ? JSON.parse(sessionStorage.getItem(`request_types_${source}`) as any)
    : undefined;
  const [types, setTypes] = useState<SelectOption | undefined>(initialTypes);

  const initialDate = sessionStorage.getItem('date')
    ? new Date(JSON.parse(sessionStorage.getItem('date') || ''))
    : undefined;

  const initialBillDate = sessionStorage.getItem('bill_date')
    ? {
        start: new Date(
          JSON.parse(sessionStorage.getItem('bill_date') || '').start
        ),
        end: new Date(
          JSON.parse(sessionStorage.getItem('bill_date') || '').end
        ),
      }
    : {
        start: undefined,
        end: undefined,
      };

  const initialCreatedDate = sessionStorage.getItem('createdDate')
    ? {
        start: new Date(
          JSON.parse(sessionStorage.getItem('createdDate') || '').start
        ),
        end: new Date(
          JSON.parse(sessionStorage.getItem('createdDate') || '').end
        ),
      }
    : {
        start: undefined,
        end: undefined,
      };

  const [date, setDate] = useState(initialDate);
  const [billdate, setBillDate] = useState(initialBillDate);
  const [createdDate, setCreatedDate] = useState(initialCreatedDate);

  const initialmeta_event_type = sessionStorage.getItem('meta_event_type')
    ? sessionStorage.getItem('meta_event_type') || ''
    : undefined;

  const [eventType, setEventType] = useState(initialmeta_event_type);

  const initialTime = sessionStorage.getItem('preferenceTime')
    ? JSON.parse(sessionStorage.getItem('preferenceTime') || '')
    : undefined;

  const initialPrescription = sessionStorage.getItem('prescription')
    ? JSON.parse(sessionStorage.getItem('prescription') || '')
    : undefined;

  const [preferenceTime, setPreferenceTime] = useState<
    undefined | {value: string; label: string}
  >(initialTime);

  const initialOTC = sessionStorage.getItem(`onlyOTC_${source}`)
    ? JSON.parse(sessionStorage.getItem(`onlyOTC_${source}`) || '')
    : undefined;

  const [onlyOTC, setOnlyOTC] = useState<
    undefined | {value: string; label: string}
  >(initialOTC);

  const initialPaid = sessionStorage.getItem(`paid_${source}`)
    ? JSON.parse(sessionStorage.getItem(`paid_${source}`) || '')
    : undefined;

  const initalRedispatch = sessionStorage.getItem('redispatch')
    ? JSON.parse(sessionStorage.getItem('redispatch') || '')
    : undefined;

  const [paid, setPaid] = useState<undefined | {value: string; label: string}>(
    initialPaid
  );
  const [redispatch, setRedispatch] = useState<
    undefined | {value: string; label: string}
  >(source !== 'app' ? initalRedispatch : undefined);
  const [prescription, setPrescription] = useState<
    undefined | {value: string; label: string}
  >(source === 'app' ? initialPrescription : undefined);

  const initialDeliveredBy = sessionStorage.getItem('deliveredBy')
    ? JSON.parse(sessionStorage.getItem('deliveredBy') || '')
    : undefined;

  const [deliveredBy, setDeliveredBy] = useState<
    undefined | {value: string; label: string}
  >(initialDeliveredBy);
  const initialIsUrgentFilter = sessionStorage.getItem('isUrgentFilter')
    ? JSON.parse(sessionStorage.getItem('isUrgentFilter') || '')
    : undefined;
  const [isUrgentFilter, setIsUrgentFilter] = useState<
    undefined | {value: string; label: string}
  >(initialIsUrgentFilter);
  const initialStatusDate = sessionStorage.getItem('statusDate')
    ? new Date(JSON.parse(sessionStorage.getItem('statusDate') || ''))
    : undefined;
  const [statusDate, setStatusDate] = useState(initialStatusDate);

  const [MMTable, setMMTable] = useState(
    MM_SCHEMA.map((item) => ({
      ...item,
      value: item.dataKey,
      label: item.header,
    }))
  );
  const [clients, setClients] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [filter, setFilter] = useState<any>({
    location: initialLocation,
    onlyOTC: initialOTC,
    paid: initialPaid,
    statuses: initialStatuses,
    types: initialTypes,
    billDate: initialBillDate,
    createdDate: initialCreatedDate,
    date: initialDate,
    preferenceTime: initialTime,
    prescription: source === 'app' ? initialPrescription : undefined,
    meta_event_type: initialmeta_event_type,
    redispatch: source !== 'app' ? initalRedispatch : undefined,
    deliveredBy: initialDeliveredBy ?? undefined,
    isUrgentFilter: initialIsUrgentFilter ?? undefined,
    statusDate: initialStatusDate ?? undefined,
  });

  const [filterVisible, setFilterVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [productIds, setProductIds] = useState<any>([]);
  const [userId, setUserId] = useState<any>(0);
  const [addressId, setAddressId] = useState<any>(0);
  const [isModalOpen, setModalOpen] = useState(false);
  const [addressVisible, setAddressVisible] = useState(false);
  const [productVisible, setProductVisible] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
  const [isUrgentDelivery, setIsUrgentDelivery] = useState(false);
  const [orderStatus, setOrderStatus] = useState('');
  const [statusesData, setStatusesData] = useState<{
    preference_time: null | string;
    preference_date: null | Date;
  }>({
    preference_time: null,
    preference_date: null,
  });

  function transformData(data: Request[], source: string) {
    return data.map((item) => ({
      ...item,
      rx_no:
        source === 'micromerchant'
          ? `${item.rx_no || ''}${item.refill_no ? ` (${item.refill_no})` : ''}
            `
          : item.rx_no,
      sync_time: get(item, 'sync_date', '') + '\n' + item.sync_time,
      bill_time:
        (get(item, 'bill_date', '') || '') + '\n' + (item.bill_time || ''),
      amount_due: `$${item.amount_due?.toFixed(2)}`,
      number: <Typography variant="strong">#M{item.id}</Typography>,
      drug_name: item?.drug_name,
      notes: item?.notes,
      preference_date:
        item.preference_date || item.preference_time
          ? `${item.preference_date || '--/--/--'} / ${
              item.preference_time || '-'
            }`
          : '',

      status:
        source === 'micromerchant' ? (
          // @ts-ignore
          <StatusBadge color={mapMMSubStatusColor[item.status]}>
            {/*// @ts-ignore*/}
            {mapMMStatusText[item.status]}
          </StatusBadge>
        ) : (
          <StatusBadge
            color={
              item.status === 'call_doctor' && item.is_resolved
                ? 'green'
                : mapStatusColor[item.status]
            }
          >
            {/*@ts-ignore*/}
            {mapStatusText[item.status]}
          </StatusBadge>
        ),
      checkbox: (
        <div
          className={classNames(styles.checkbox, {
            [styles['--disabled']]: disabledStatus.includes(item.status),
          })}
          onClick={(val) => {
            val.stopPropagation();
            if (disabledStatus.includes(item.status)) {
              return;
            }

            const include = selectedOrders.find((i) => i === item.id);

            if (include) {
              setSelectedOrders(selectedOrders.filter((i) => i !== item.id));
            } else {
              setSelectedOrders((pr) => [...pr, item.id]);
            }
          }}
        >
          <div className={styles.tooltip}>
            You can't select delivered orders
          </div>
          <CheckIcon
            emptySelect={!selectedOrders.find((i) => i === item.id)}
            allSelected={!!selectedOrders.find((i) => i === item.id)}
          />
        </div>
      ),
      view: <ViewDetails to={`/requests/${item.id}`} />,
    }));
  }

  const filtered = useMemo(() => {
    return (
      (filter.location.length > 0 ||
        filter.statuses.length > 0 ||
        !!filter.billDate?.start ||
        !!filter.meta_event_type ||
        !!filter.createdDate?.start ||
        !!filter.date ||
        !!filter.preferenceTime ||
        !!filter.onlyOTC ||
        !!filter.redispatch ||
        !!filter.paid ||
        !!filter.prescription ||
        !!filter.types ||
        !!filter.deliveredBy ||
        !!filter.isUrgentFilter ||
        !!filter.statusDate) &&
      !filterVisible
    );
  }, [filter, filterVisible]);

  const resetFilter = () => {
    setFilterVisible(false);
    const data = {
      location: [],
      onlyOTC: undefined,
      paid: undefined,
      statuses: [],
      billDate: {
        start: undefined,
        end: undefined,
      },
      createdDate: {
        start: undefined,
        end: undefined,
      },
      date: undefined,
      preferenceTime: undefined,
      meta_event_type: undefined,
      redispatch: undefined,
      deliveredBy: undefined,
      isUrgentFilter: undefined,
      statusDate: undefined,
    };
    setFilter(data);
    filterHandler(data);
  };

  const filterHandler = (filter: any) => {
    setLocation(filter.location);
    setTypes(filter.types);
    setOnlyOTC(filter.onlyOTC);
    setPaid(filter.paid);
    setBillDate(filter.billDate);
    setCreatedDate(filter.createdDate);
    setDate(filter.date);
    setPreferenceTime(filter.preferenceTime);
    setStatuses(filter.statuses);
    setPrescription(filter.prescription);
    setEventType(filter.meta_event_type);
    setRedispatch(filter.redispatch);
    setDeliveredBy(filter.deliveredBy);
    setIsUrgentFilter(filter.isUrgentFilter);
    setStatusDate(filter.statusDate);
  };

  const applyFilter = () => {
    setFilterVisible(false);
    filterHandler(filter);
    setCurrentPage(1);
    history.push(
      source === 'micromerchant' ? '/orders/page/1' : '/requests/page/1'
    );
  };

  const onSearch = (val: string) => {
    setKeyWord(val);
    setCurrentPage(1);
    if (params.page !== '1') {
      if (source === 'app') {
        history.push('/requests/page/1');
      } else {
        history.push('/orders/page/1');
      }
    }
    sessionStorage.setItem(`searchKey_${source}`, val);
  };

  const getLocalTable = () => {
    let localTable = localStorage.getItem('tableData');
    if (localTable) {
      const data: any[] = MM_SCHEMA.filter((item) => {
        if (item.dataKey === 'checkbox') {
          return true;
        }
        return JSON.parse(localTable || '').find(
          (el: any) => el.dataKey === item.dataKey
        );
      });
      setMMTable(
        data.map((item) => ({...item, value: item.dataKey, label: item.header}))
      );
    }
  };

  const onChangeTab = (val: any) => {
    if (val) {
      const newTabs = MMTable.some((item) => item.label === val.label)
        ? reject(MMTable, val)
        : [...MMTable, val];

      setMMTable(sortLodash(newTabs, 'index'));

      localStorage.setItem(
        'tableData',
        JSON.stringify(sortLodash(newTabs, 'index'))
      );
    }
  };

  useEffect(() => {
    getLocalTable();
  }, []);

  useEffect(() => {
    if (params.page) {
      setCurrentPage(parseInt(params.page));
    }
  }, [params.page]);

  useEffect(() => {
    const timeOutId = setTimeout(() => {
      setApiKeyWord(key);
    }, 600);
    return () => clearTimeout(timeOutId);
  }, [key]);
  // console.log(source);
  useEffect(() => {
    sessionStorage.setItem(`paid_${source}`, paid ? JSON.stringify(paid) : '');
    sessionStorage.setItem(
      `onlyOTC_${source}`,
      onlyOTC ? JSON.stringify(onlyOTC) : ''
    );
    sessionStorage.setItem(
      `preferenceTime_${source}`,
      preferenceTime ? JSON.stringify(preferenceTime) : ''
    );
    sessionStorage.setItem('date', date ? JSON.stringify(date) : '');
    sessionStorage.setItem(
      'bill_date',
      billdate.start ? JSON.stringify(billdate) : ''
    );
    sessionStorage.setItem(
      'created_date',
      createdDate.start ? JSON.stringify(createdDate) : ''
    );
    sessionStorage.setItem(
      'request_statuses',
      JSON.stringify(statuses.length > 0 ? statuses : [])
    );

    sessionStorage.setItem(
      `location_${source}`,
      location ? JSON.stringify(location) : ''
    );
    sessionStorage.setItem(
      `request_types_${source}`,
      types ? JSON.stringify(types) : ''
    );
    sessionStorage.setItem(
      'isUrgentFilter',
      isUrgentFilter ? JSON.stringify(isUrgentFilter) : ''
    );
    sessionStorage.setItem(
      'statusDate',
      statusDate ? JSON.stringify(statusDate) : ''
    );
    if (source === 'app') {
      sessionStorage.setItem(
        `prescription`,
        prescription ? JSON.stringify(prescription) : ''
      );
    }
    if (source !== 'app') {
      sessionStorage.setItem(
        'redispatch',
        redispatch ? JSON.stringify(redispatch) : ''
      );
    }
    sessionStorage.setItem(
      'deliveredBy',
      deliveredBy ? JSON.stringify(deliveredBy) : ''
    );
    sessionStorage.setItem(`meta_event_type`, eventType ? eventType : '');
  }, [
    paid,
    onlyOTC,
    preferenceTime,
    date,
    billdate,
    statuses,
    location,
    types,
    source,
    createdDate,
    prescription,
    eventType,
    redispatch,
    deliveredBy,
    isUrgentFilter,
    statusDate,
  ]);

  const {data, refetch, isLoading} = useQuery(
    [
      'requests',
      String(currentPage - 1 || 0),
      order,
      status,
      filter.userId || '',
      keyWord,
      source,
      date,
      billdate,
      createdDate,
      location,
      preferenceTime,
      onlyOTC,
      paid,
      statuses,
      types,
      prescription,
      eventType,
      redispatch,
      pageSize,
      deliveredBy,
      isUrgentFilter,
      statusDate,
    ],
    getRequests
  );
  const onChangeData = (val: any) => {
    setDate(val);
  };

  const onChangeStatuses = (value: any) => {
    if (value) {
      setStatuses((prev) =>
        prev.some((item) => item.label === value.label)
          ? reject(prev, value)
          : [...prev, value]
      );
    } else {
      setStatuses([]);
    }
  };
  const onChangeType = (value: any) => {
    setTypes(value);
  };

  const getProducts = async (page: number, keyWord: string) => {
    try {
      const {data} = await api.get<ListResponse<Product>>(`/products/`, {
        params: {
          limit: 50,
          offset: (page - 1) * 50,
          search: keyWord,
        },
      });
      setProducts((pr) =>
        page === 1 ? data.results : pr.concat(data.results)
      );
    } catch (error) {}
  };

  const getClients = async (page: number, keyWord: string) => {
    try {
      const {data} = await api.get<ListResponse<Customer>>(`/customers/`, {
        params: {
          limit: 50,
          offset: (page - 1) * 50,
          search: keyWord,
        },
      });
      setClients((pr) => (page === 1 ? data.results : pr.concat(data.results)));
    } catch (error) {}
  };

  const getAddress = async (id: number) => {
    setAddresses([]);
    try {
      const {data} = await api.get<Customer>(`/customers/${id}/`);
      setAddresses(data.addresses || []);
    } catch (error) {}
  };

  const handleNavigateRequest = useCallback(
    (row: DataItem) => {
      if (source === 'app') {
        history.push(`/requests/${row.id}`);
      } else {
        history.push(`/orders/${row.id}`);
      }
    },
    [history, source]
  );

  const onSubmit = () => {
    setSubmitting(true);
    api
      .post(source === 'app' ? 'requests/create/' : 'requests/create-order/', {
        customer_id: userId,
        address_id: addressId,
        drugs: productIds,
      })
      .then(({data}) => {
        refetch();
        setUserId(0);
        setAddressId(0);
        setProductIds([]);
        handleNavigateRequest(data);
        setVisible(false);
      })
      .catch((err) => {
        toast.error(err.message);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const submitOrderStatutes = async () => {
    try {
      await api.post('/requests/requests/bulk-update/', {
        request_ids: selectedOrders,
        status: orderStatus,
        preference_date: statusesData.preference_date
          ? format(statusesData.preference_date, 'yyyy-MM-dd')
          : undefined,
        preference_time: statusesData.preference_time || undefined,
        is_urgent_delivery: isUrgentDelivery,
      });
      refetch();
      setSelectedOrders([]);
      setShowStatusModal(false);
      setOrderStatus('');
      setStatusesData({
        preference_date: null,
        preference_time: null,
      });
      toast.success('Successfully updated statuses!');
    } catch (error) {
      toast.error(
        JSON.stringify(get(error, 'response.data')) ||
          'Something wrong with creation. Try again later !'
      );
    } finally {
      setIsUrgentDelivery(false);
    }
  };

  return {
    SCHEMA,
    MM_SCHEMA,
    MMTable,
    setMMTable,
    onChangeTab,
    clients,
    products,
    addresses,
    setClients,
    setProducts,
    setAddresses,
    getClients,
    getProducts,
    getAddress,
    filter,
    setFilter,
    resetFilter,
    filterVisible,
    setFilterVisible,
    applyFilter,
    filtered,
    onSubmit,
    submitting,
    handleNavigateRequest,
    currentPage,
    setCurrentPage,
    data: data && !isLoading ? transformData(data.results, source) : [],
    totalPages:
      data?.count && data.limit ? Math.ceil(data.count / data.limit) : 0,
    count: data?.count || 0,
    hasOtc: data?.has_otc,
    dailyCount: data?.dailyCount,
    loading: isLoading,
    processingTime: get(data, 'prescriptions_average_processing_time', 0),
    sortBy,
    sortOrder,
    tabs,
    MMtabs,
    status,
    keyWord: key,
    setKeyWord,
    onSort,
    setStatus,
    source,
    onChangeData,
    location,
    preferenceTime,
    paid,
    onChangeStatuses,
    statuses,
    visible,
    setVisible,
    onChangeType,
    types,
    pageSize,
    setPageSize,
    onSearch,
    productVisible,
    setProductVisible,
    addressVisible,
    setAddressVisible,
    isModalOpen,
    setModalOpen,
    productIds,
    setProductIds,
    userId,
    setUserId,
    addressId,
    setAddressId,
    history,
    rolePermission,
    selectedOrders,
    showStatusModal,
    setShowStatusModal,
    orderStatus,
    setOrderStatus,
    statusesData,
    setStatusesData,
    submitOrderStatutes,
    setIsUrgentDelivery,
    isUrgentDelivery,
  };
}

export default useRequests;
